/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useMemo } from 'react'
import styled, { css } from 'styled-components'
import {
  Text, Icon
} from 'components/atoms'
import { PopoverActions, Tooltip } from 'components/molecules'
import PropTypes from 'prop-types'
import { transparentize, darken } from 'polished'
import theme from 'services/theme.semantic'
import withInlineEdit from 'services/withInlineEdit'
import { useParams } from 'react-router-dom'
import { generateEditorPath } from 'services/generators'
import { getPageFromSlug } from 'services/requests'
import { toast } from 'react-toastify';
import { Toast } from 'containers';

const TextEditable = withInlineEdit(Text)

const SideNavEditorItem = ({
  title,
  icon,
  provided,
  snapshot,
  depth,
  item,
  removeItem,
  tree,
  isCurrent,
  onChangeTitle,
}) => {
  const [isHover, setHover] = useState(false)
  const [hasPopover, setHasPopover] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [titleFocusTrigger, setTitleFocusTrigger] = useState(false)
  const params = useParams()

  const isActive = useMemo(
    () => isHover || snapshot.isDragging || hasPopover || isEditing,
    [isHover, snapshot.isDragging, hasPopover, isEditing]
  )

  const handleOpenPageEditor = async (item) => {
    const { data } = item
    const { subweb } = params
    if ('id' in data) {
      window.open(generateEditorPath({ subweb, pageId: data.id }), '_blank')
      return;
    }

    // fetch id from slug 'url'
    if ('url' in data) {
      try {
        const { data: { pageId } } = await getPageFromSlug({ slug: data.url, subweb })
        window.open(generateEditorPath({ subweb, pageId }), '_blank')
      } catch (err) {
        toast.error(
          <Toast
            type="error"
            title="Erro a abrir editor da página"
            description={err.message}
          />
        );
      }
    }
  }

  // validations
  const mustBeParent = useMemo(() => Boolean(item && item.data && item.data.url) === false, [item])
  const isOverLimit = useMemo(() => {
    const root = tree.items[tree.rootId]
    const parents = root.children
    if (parents.includes(item.id)) {
      return parents.findIndex(parentId => parentId === item.id) > 5
    }
    return false
  }, [item, tree])

  const hasChildren = useMemo(() => item.children.length > 0, [item])

  const isInvalidItem = useMemo(() => (depth === 0 && isOverLimit)
    || (depth === 1 && mustBeParent)
    || depth > 1
    || (depth === 0 && mustBeParent && !hasChildren && !isOverLimit),
  [depth, isOverLimit, mustBeParent, hasChildren])


  const actions = useMemo(() => {
    let computed = [
      {
        text: 'Editar título',
        onClick: () => {
          setTitleFocusTrigger(!titleFocusTrigger)
        },
      },
    ]

    if ((item && item.data) && (item.data.url && item.data.id)) {
      computed = [
        ...computed,
        {
          text: 'Editar página',
          onClick: () => handleOpenPageEditor(item),
        },
      ]
    }
    return [
      ...computed,
      {
        text: 'Remover',
        onClick: () => {
          removeItem(item)
        },
      }
    ]
  }, [tree, item])

  const handleTitleChange = (e) => {
    onChangeTitle(item, e.target.value)
  }

  const handleTitleFocus = () => {
    setIsEditing(true)
  }

  const handleTitleBlur = () => {
    setIsEditing(false)
  }

  return (
    <NavTitle
      ref={provided.innerRef}
      {...provided.draggableProps}
      isDragging={snapshot.isDragging}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      isActive={isActive}
      depth={depth}
      isDanger={isInvalidItem}
      {...provided.dragHandleProps}
    >
      <Inner>
        <Inner.Start>
          <DragHandler isVisible={isActive}>
            <Icon icon="drag-handle" height={15} color="#A2A2A2" />
          </DragHandler>
          {depth === 0 && mustBeParent && !hasChildren && !isOverLimit && (
            <Tooltip
              tooltip="Este item não tem um link, tem de obrigatoriamente ter filhos. Adicione uma página dentro deste item."
              placement="bottom"
            >
              <Warning>
                <Icon icon="warn" height={18} color={theme.colors.dangerDarker} />
              </Warning>
            </Tooltip>
          )}
          {depth === 0 && isOverLimit && (
            <Tooltip
              tooltip="Este item ultrapassa o limite de 6 items visíveis no menu. Não será visível."
              placement="bottom"
            >
              <Warning>
                <Icon icon="warn" height={18} color={theme.colors.dangerDarker} />
              </Warning>
            </Tooltip>
          )}
          {depth === 1 && mustBeParent && (
            <Tooltip
              tooltip="Este item não tem um link, tem de obrigatoriamente ser o primeiro item."
              placement="bottom"
            >
              <Warning>
                <Icon icon="warn" height={18} color={theme.colors.dangerDarker} />
              </Warning>
            </Tooltip>
          )}
          {depth > 1 && (
            <Tooltip
              tooltip="Limite de filhos excedido, este item não vai estar vísivel. Se pretende que seja vísivel arraste-o para cima."
              placement="bottom"
            >
              <Warning>
                <Icon icon="warn" height={18} color={theme.colors.dangerDarker} />
              </Warning>
            </Tooltip>
          )}
          <TextEditable
            onChange={handleTitleChange}
            onFocus={handleTitleFocus}
            onBlur={handleTitleBlur}
            focusTrigger={titleFocusTrigger}
            modifiers={isCurrent ? ['weightBase'] : []}
          >
            {title}
          </TextEditable>
          {icon}
        </Inner.Start>
        <Inner.End isVisible={isActive}>
          <PopoverActions
            handlerWidth={16}
            iconHeight={16}
            buttonModifiers={['xs3']}
            onChangeOpen={(isOpen) => setHasPopover(isOpen)}
            actions={actions}
          />
        </Inner.End>
      </Inner>
    </NavTitle>
  )
}

const Inner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

Inner.Start = styled.div`
  display: flex;
  align-items: center;
`

Inner.End = styled.div`
  display: flex;
  align-items: center;
  transition: opacity 0.2s ease;
  opacity: ${props => (props.isVisible ? '1' : '0.2')};
  padding-right: ${props => props.theme.spacing.xs2};
`

const DragHandler = styled.div`
  transition: opacity 0.2s ease;
  opacity: ${props => (props.isVisible ? '1' : '0')};
  display: flex;
  align-items: center;
  padding: ${props => props.theme.spacing.xs2} ${props => props.theme.spacing.xs};
  cursor: grab;
`

const NavTitle = styled.div(props => css`
  display: flex;
  align-items: center;
  padding-right:  ${props.theme.spacing.xs};
  padding-top:  ${props.theme.spacing.xs3};
  padding-bottom:  ${props.theme.spacing.xs3};
  cursor: default;
  background-color: ${props => {
    if (props.isActive) return props.theme.colors.bg
    if (!props.isDanger && props.depth > 0) return darken(0.02, props.theme.colors.bgLighterS)
    return (
      props.isDanger
        ? transparentize(0.94, props.theme.colors.danger)
        : props.theme.colors.bgLighterS
    )
  }};
  ${props => props.isDragging && css`
    box-shadow: ${props => props.theme.shadows.card};
    background-color: ${props => props.theme.colors.bg} !important;
  `};
  &:hover, &:focus {
    background-color: ${props.theme.colors.bg};
  }
  &:active {
    background-color: ${props.theme.colors.bgLighterS};
  }
`)

const Warning = styled.div`
  padding-right: ${props => props.theme.spacing.xs};
  cursor: help;
`

SideNavEditorItem.propTypes = {
  title: PropTypes.string,
  icon: PropTypes.node,
  provided: PropTypes.shape({
    dragHandleProps: PropTypes.shape({}),
    draggableProps: PropTypes.shape({}),
    innerRef: PropTypes.oneOfType([
      PropTypes.func,
      PropTypes.shape({ current: PropTypes.any })
    ])
  }).isRequired,
  snapshot: PropTypes.shape({
    isDragging: PropTypes.bool
  }).isRequired,
  depth: PropTypes.number.isRequired,
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    data: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      url: PropTypes.string,
    }),
    children: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  removeItem: PropTypes.func,
  tree: PropTypes.shape({
    items: PropTypes.arrayOf(PropTypes.shape({})),
    rootId: PropTypes.string
  }),
  isCurrent: PropTypes.bool,
  onChangeTitle: PropTypes.func,
}

SideNavEditorItem.defaultProps = {
  title: 'title',
  icon: null,
  removeItem: () => { },
  tree: null,
  isCurrent: false,
  onChangeTitle: () => { }
}

export default SideNavEditorItem
